import { useQuery } from '@tanstack/react-query'
import { LineChart } from '@/components/charts/line-chart'
import { Line } from '@/components/charts/line'
import { Grid } from '@/components/charts/grid'
import { XAxis } from '@/components/charts/x-axis'
import { ChartTooltip } from '@/components/charts/tooltip/chart-tooltip'
import { useAppStore } from '@/store'
import {
  fetchTaxRevenues,
  fetchMaastricht,
  fetchDeficitSeries,
  type TaxItem,
} from '@/data/eurostat'
import { useNavigate } from 'react-router'
import SankeyDiagram from '@/components/SankeyDiagram'

function formatMd(value: number | null | undefined): string {
  if (value == null) return '—'
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(0).replace('.', ' ')} Md€`
  }
  return `${value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} M€`
}

function KpiCard({ title, value, subtitle, color = 'text-nav', onClick }: {
  title: string
  value: string
  subtitle: string
  color?: string
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="bg-card rounded-lg shadow p-6 text-left hover:shadow-lg transition-shadow cursor-pointer border border-transparent hover:border-border"
    >
      <h3 className="font-semibold text-lg text-text">{title}</h3>
      <p className={`text-3xl font-bold ${color} mt-2`}>{value}</p>
      <p className="text-sm text-text-muted mt-1">{subtitle}</p>
    </button>
  )
}

function TaxBreakdown({ taxes, loading }: { taxes: TaxItem[]; loading: boolean }) {
  if (loading) {
    return <div className="mt-8 bg-card rounded-lg shadow p-6">Chargement des données fiscales…</div>
  }
  if (taxes.length === 0) return null

  return (
    <div className="mt-8 bg-card rounded-lg shadow p-6">
      <h3 className="font-semibold text-lg mb-4">Répartition des recettes fiscales</h3>
      <div className="space-y-3">
        {taxes
          .sort((a, b) => b.value - a.value)
          .map((tax) => {
            const isWide = tax.pctOfTotal >= 8
            return (
              <div key={tax.code} className="flex items-center gap-3">
                <div className="w-40 text-sm font-medium text-right text-text">{tax.shortLabel}</div>
                <div className="flex-1 bg-muted rounded-full h-6 relative overflow-hidden">
                  <div
                    className="bg-nav h-full rounded-full transition-all duration-700"
                    style={{ width: `${tax.pctOfTotal}%` }}
                  />
                  <span
                    className={`absolute inset-0 flex items-center justify-center text-xs font-medium ${
                      isWide ? 'text-white' : 'text-text'
                    }`}
                  >
                    {tax.pctOfTotal.toFixed(1)}%
                  </span>
                </div>
                <div className="w-32 text-sm font-semibold text-right text-text">
                  {formatMd(tax.value)}
                </div>
              </div>
            )
          })}
      </div>
      <p className="text-xs text-text-muted mt-4">
        Source : Eurostat gov_10a_taxag (M€, monnaie nationale). Cliquez pour le détail.
      </p>
    </div>
  )
}

function DeficitChart({ data }: { data: Array<{ year: number; value: number | null }> }) {
  if (data.length === 0) return null

  const chartData = data.map((d) => ({ year: d.year, value: d.value ?? 0 }))

  return (
    <div className="mt-8 bg-card rounded-lg shadow p-6">
      <h3 className="font-semibold text-lg mb-4">Évolution du déficit public (% du PIB)</h3>
      <LineChart data={chartData} xDataKey="year" aspectRatio="2 / 1">
        <Grid horizontal />
        <Line dataKey="value" stroke="#c73652" showMarkers />
        <XAxis />
        <ChartTooltip />
      </LineChart>
      <p className="text-xs text-text-muted mt-2">
        Source : Eurostat gov_10dd_edpt1 (solde B9, % du PIB, S13)
      </p>
    </div>
  )
}

export default function Dashboard() {
  const { selectedYear } = useAppStore()
  const navigate = useNavigate()

  // KPI data
  const maastrichtQuery = useQuery({
    queryKey: ['maastricht', selectedYear],
    queryFn: () => fetchMaastricht(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  // Tax breakdown
  const taxesQuery = useQuery({
    queryKey: ['taxes', selectedYear],
    queryFn: () => fetchTaxRevenues(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  // Deficit time series
  const deficitQuery = useQuery({
    queryKey: ['deficit-series'],
    queryFn: () => fetchDeficitSeries(2015, 2023),
    staleTime: 1000 * 60 * 60,
  })

  const m = maastrichtQuery.data
  const totalTaxes = taxesQuery.data?.reduce((s, t) => s + t.value, 0) ?? 0

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Vue d'ensemble — Finances publiques françaises</h2>
        <p className="text-text-muted mt-1">
          Données Eurostat pour l'année {selectedYear} — Administrations publiques (S13)
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Prélèvements obligatoires"
          value={formatMd(totalTaxes)}
          subtitle="Impôts + cotisations sociales"
          onClick={() => navigate('/recettes')}
        />
        <KpiCard
          title="Total dépenses"
          value={formatMd(m?.totalExpenditure)}
          subtitle="Ensemble des APU"
          onClick={() => navigate('/depenses')}
        />
        <KpiCard
          title="Déficit public"
          value={m?.deficitPctGdp != null ? `${m.deficitPctGdp}% du PIB` : '—'}
          subtitle={formatMd(null)} // absolute value not shown here for clarity
          color="text-highlight"
        />
        <KpiCard
          title="Dette publique"
          value={m?.debtPctGdp != null ? `${m.debtPctGdp}% du PIB` : '—'}
          subtitle={formatMd(m?.debtAbsolute)}
        />
      </div>

      {/* Tax breakdown */}
      <TaxBreakdown
        taxes={taxesQuery.data ?? []}
        loading={taxesQuery.isLoading}
      />

      {/* Deficit evolution chart */}
      {deficitQuery.data && <DeficitChart data={deficitQuery.data} />}

      {/* Sankey */}
      <div className="mt-8 bg-card rounded-lg shadow p-6">
        <h3 className="font-semibold text-lg mb-4">Flux Recettes → Dépenses</h3>
        <p className="text-sm text-text-muted mb-4">
          Comment chaque type d'impôt et cotisation alimente les différentes fonctions de l'État
        </p>
        <SankeyDiagram />
        <p className="text-xs text-text-muted mt-4">
          Sources : Eurostat gov_10a_taxag (recettes) + gov_10a_exp (COFOG). Les liens sont une approximation proportionnelle.
        </p>
      </div>
    </div>
  )
}
